import {Component, computed, effect, inject, signal, untracked} from '@angular/core';
import {firstValueFrom} from 'rxjs';
import {TranslocoPipe} from '@jsverse/transloco';
import {Card} from 'primeng/card';
import {Tab, TabList, TabPanel, TabPanels, Tabs} from 'primeng/tabs';
import {
  AuditInfo,
  CompanyInfoResponse,
  ProjectRoleType,
  ProjectStepType,
} from '@frontend/shared-api';
import {CountryService} from '@frontend/shared';
import {DirectusSelect} from '@frontend/directus';
import {AuditInfoComponent} from '../../../../../../shared/components/audit-info/audit-info.component';
import {StepDotMenuComponent} from '../../../../../../shared/components/step-dot-menu/step-dot-menu.component';
import {TabStatusDirective} from '../../../../../../shared/tabs/tab-status.directive';
import {TabStatusEnum} from '../../../../../../shared/tabs/tab-status.model';
import {TabsUnsavedRevealDirective} from '../../../../../../shared/tabs/tabs-unsaved-reveal.directive';
import {ProjectBaseStepComponent} from '../../../project-base-step/project-base-step.component';
import {unitStatus} from './company-info.calc';
import {CompanySummaryComponent} from './company-summary/company-summary.component';
import {CompanyUnitFormComponent} from './company-unit-form/company-unit-form.component';
import {Step11CompanyInformationService, toCompanyInfoValues} from './step-1-1.service';
import {
  CompanyInfoValues,
  EMPTY_COMPANY_INFO_VALUES,
  GERMANY_COUNTRY_CODE,
  Office,
  SectionStatus,
  UnitSummary,
} from './step-1-1-company-information.types';

interface UnitTab {
  office: Office;
  index: number;
  savedValues: CompanyInfoValues;
  status: SectionStatus;
  tabStatus: TabStatusEnum;
}

const TAB_STATUS: Record<SectionStatus, TabStatusEnum> = {
  empty: TabStatusEnum.NOT_STARTED,
  'in-progress': TabStatusEnum.IN_PROGRESS,
  complete: TabStatusEnum.COMPLETED,
};

@Component({
  selector: 'app-1-1-company-information',
  imports: [
    TranslocoPipe,
    Tabs,
    TabList,
    Tab,
    TabPanels,
    TabPanel,
    Card,
    StepDotMenuComponent,
    AuditInfoComponent,
    TabStatusDirective,
    TabsUnsavedRevealDirective,
    CompanyUnitFormComponent,
    CompanySummaryComponent,
  ],
  templateUrl: './step-1-1-company-information.component.html',
})
export class Step11CompanyInformationComponent extends ProjectBaseStepComponent {
  protected readonly contributorRole = ProjectRoleType.CONTRIBUTOR;
  protected readonly companyInformationStep = ProjectStepType.COMPANY_INFORMATION;

  readonly countryService = inject(CountryService);
  private readonly step11Service = inject(Step11CompanyInformationService);

  protected readonly offices = signal<Office[]>([]);
  protected readonly activeTab = signal(0);
  protected readonly savedValuesByOffice = signal<Map<number, CompanyInfoValues>>(new Map());
  protected readonly currentValuesByOffice = signal<Map<number, CompanyInfoValues>>(new Map());
  protected readonly industryDropdown = signal<DirectusSelect | null>(null);
  protected readonly tariffDropdown = signal<DirectusSelect | null>(null);
  protected readonly auditInfo = signal<AuditInfo | null>(null);

  protected readonly summaryTabValue = computed(() => this.offices().length);
  protected readonly unitTabs = computed<UnitTab[]>(() => this.buildUnitTabs());
  protected readonly unitSummaries = computed<UnitSummary[]>(() =>
    this.unitTabs().map((tab) => ({
      office: tab.office,
      values: this.currentValues(tab.office),
      status: tab.status,
    })),
  );
  protected readonly summaryTabStatus = computed(() => {
    const tabs = this.unitTabs();
    const allComplete = tabs.length > 0 && tabs.every((tab) => tab.status === 'complete');
    return allComplete ? TabStatusEnum.COMPLETED : TabStatusEnum.NOT_STARTED;
  });

  constructor() {
    super();
    effect(() => this.syncOffices());
  }

  onUnitValuesChange(officeId: number, values: CompanyInfoValues): void {
    this.currentValuesByOffice.update((current) => new Map(current).set(officeId, values));
  }

  async onUnitSaved(response: CompanyInfoResponse): Promise<void> {
    this.auditInfo.set(response.auditInfo ?? null);
    await this.stepState.loadStepStatuses(this.projectId);
  }

  private syncOffices(): void {
    const project = this.projectDataService.getProject()();
    if (!project) {
      return;
    }
    untracked(() => {
      this.projectId = project.id;
      const offices: Office[] = Array.from(project.offices ?? []).map((office) => ({
        id: office.officeId,
        name: office.officeName,
        countryCode: office.countryCode,
        cityName: office.cityName,
      }));
      this.offices.set(offices);
      void this.loadData(offices);
    });
  }

  private async loadData(offices: Office[]): Promise<void> {
    const [industryDropdown, tariffDropdown] = await Promise.all([
      firstValueFrom(this.step11Service.loadIndustryDropdown()),
      firstValueFrom(this.step11Service.loadTariffDropdown()),
    ]);
    this.industryDropdown.set(industryDropdown);
    this.tariffDropdown.set(tariffDropdown);

    const responses = await Promise.all(
      offices.map((office) => this.step11Service.loadCompanyInfo(this.projectId, office.id)),
    );
    this.savedValuesByOffice.set(
      new Map(responses.map((response) => [response.officeId, toCompanyInfoValues(response)])),
    );
    this.auditInfo.set(latestAuditInfo(responses));
  }

  private buildUnitTabs(): UnitTab[] {
    const savedValuesByOffice = this.savedValuesByOffice();
    return this.offices().map((office, index) => {
      const status = unitStatus(
        this.currentValues(office),
        office.countryCode === GERMANY_COUNTRY_CODE,
      );
      return {
        office,
        index,
        savedValues: savedValuesByOffice.get(office.id) ?? EMPTY_COMPANY_INFO_VALUES,
        status,
        tabStatus: TAB_STATUS[status],
      };
    });
  }

  private currentValues(office: Office): CompanyInfoValues {
    return (
      this.currentValuesByOffice().get(office.id) ??
      this.savedValuesByOffice().get(office.id) ??
      EMPTY_COMPANY_INFO_VALUES
    );
  }
}

function latestAuditInfo(responses: CompanyInfoResponse[]): AuditInfo | null {
  return (
    responses
      .map((response) => response.auditInfo ?? null)
      .filter((auditInfo): auditInfo is AuditInfo => auditInfo !== null)
      .sort((left, right) =>
        String(right.lastUpdatedAt).localeCompare(String(left.lastUpdatedAt)),
      )[0] ?? null
  );
}
